
<template>
    <div>
        <div class="p-mb-3">
            <Dropdown v-model="selectedTourId" :options="tourOptions" optionLabel="label" optionValue="value"
                placeholder="Sélectionner une tournée à préparer" class="w-full custom-dropdown" />
        </div>
        <ProgressBar v-if="selectedTourDetails" :value="progressValue"
            :class="allProductsPrepared ? 'p-progressbar-success' : 'p-progressbar-info'">
            {{ progressValue }}%
        </ProgressBar>
        <div v-if="selectedTourDetails">
            <h5>Détails de la tournée :</h5>
            <Listbox v-model="selectedProducts" :options="selectedTourDetails.products" optionLabel="name"
                optionValue="name" class="w-full" multiple :class="{ 'p-listbox-highlight-selected': true }"
                @change="handleProductSelection">
                <template #option="slotProps">
                    <div :class="['product-item', { 'selected': isProductSelected(slotProps.option) }]"
                        @click="toggleProductPreparation(slotProps.option)">
                        <Tag :value="slotProps.option.quantity"
                            :severity="slotProps.option.prepared ? 'success' : 'warning'"
                            class="p-mr-2 custom-quantity-badge" />
                        <span class="product-name">x {{ slotProps.option.name }}</span>
                    </div>
                </template>
            </Listbox>
        </div>
        <div v-if="selectedTourDetails" class="p-mb-3 upload-buttons-container">
            <FileUpload label="Recto de la palette" @update:imageIds="updatePhoto1"
                :resetTrigger="resetFileUploads" />
            <FileUpload label="Verso de la palette" @update:imageIds="updatePhoto2"
                :resetTrigger="resetFileUploads" />
        </div>
        <div class="p-mt-4" v-if="selectedTourDetails">
            <Button :label="allChecksCompleted ? 'Marquer la tournée comme préparée' : 'Vérifications en cours...'"
                :class="allChecksCompleted ? 'p-button-success' : 'p-button-outlined'" :disabled="!allChecksCompleted"
                class="p-button-lg w-full" @click="markTourAsPrepared" />
        </div>
        <Toast ref="toastRef" />
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import axios from '@/api/axios';
import FileUpload from '@/views/admin/Tournees/RetoursTournees/FileUpload.vue';

const toastRef = ref();
const toursData = ref([]);
const tourOptions = ref([]);
const selectedTourId = ref(null);
const selectedTourDetails = ref(null);
const photo1Ids = ref([]);
const photo2Ids = ref([]);
const selectedProducts = ref([]);
const resetFileUploads = ref(false);

const allProductsPrepared = computed(() => {
    return selectedTourDetails.value?.products.every(product => product.prepared) ?? false;
});

const allChecksCompleted = computed(() => {
    return allProductsPrepared.value && photo1Ids.value.length > 0 && photo2Ids.value.length > 0;
});

const progressValue = computed(() => {
    if (!selectedTourDetails.value?.products) return 0;
    const totalProducts = selectedTourDetails.value.products.length;
    const preparedCount = selectedTourDetails.value.products.filter(p => p.prepared).length;
    return Math.round((preparedCount / totalProducts) * 100);
});

const fetchTourData = async () => {
    try {
        const today = new Date().toISOString().split('T')[0];
        const { data } = await axios.get(`admin-explo/tournees/?start_date=${today}&end_date=${today}`);
        toursData.value = data;
        const filteredTours = toursData.value.filter(tour => !tour.isPrepared);
        
        // Modification: Remplacer "TEL" par "TOURNEE" dans les noms
        tourOptions.value = filteredTours.length > 0 
            ? filteredTours.map(tour => {
                let displayName = tour.tournee_name;
                
                // Remplacer "TEL" par "TOURNEE" dans le nom de la tournée
                if (displayName.startsWith('TEL')) {
                    displayName = 'TOURNEE' + displayName.substring(3);
                }
                
                return {
                    value: tour.id,
                    label: displayName,
                    originalName: tour.tournee_name // Conserver le nom original pour la référence
                };
            })
            : [{
                value: null,
                label: "Aucune tournée à préparer"
            }];
    } catch (error) {
        const errorMessage = error.response?.data?.error ?? 'Impossible de charger les données des tournées.';
        toastRef.value.add({ severity: 'error', summary: 'Erreur', detail: errorMessage, life: 5000 });
        console.error('Erreur lors de la récupération des données des tournées:', error);
    }
};

const fetchTourDetails = async (tourId) => {
  try {
    const { data } = await axios.get(`admin-explo/tournees/${tourId}/`);
    
    // Trouver l'option sélectionnée pour obtenir le nom d'affichage
    const selectedTour = tourOptions.value.find(option => option.value === tourId);
    const displayName = selectedTour?.label || data.tournee_name;
    
    selectedTourDetails.value = {
      ...data,
      displayName, // Utiliser le nom modifié pour l'affichage
      products: data.tour_products
        .filter(product => !product.package__name.includes('cageot'))
        .map(product => ({
          name: product.package__name,
          quantity: product.total_quantity,
          prepared: false,
        }))
    };
    selectedProducts.value = [];
  } catch (error) {
    const errorMessage = error.response?.data?.error ?? 'Erreur lors du chargement des détails';
    toastRef.value.add({ severity: 'error', summary: 'Erreur', detail: errorMessage, life: 5000 });
    console.error('Erreur détails tournée:', error);
  }
};



const handleProductSelection = (event) => {
    selectedProducts.value = event.value;
    updateProductsPreparedStatus();
};

const toggleProductPreparation = (product) => {
    product.prepared = !product.prepared;
    updateProductsPreparedStatus();
};

const updateProductsPreparedStatus = () => {
    selectedTourDetails.value.products.forEach(product => {
        product.prepared = selectedProducts.value.includes(product.name);
    });
};

const isProductSelected = (product) => {
    return selectedProducts.value.includes(product.name);
};

const updatePhoto1 = (ids) => photo1Ids.value = ids;
const updatePhoto2 = (ids) => photo2Ids.value = ids;

const resetForm = () => {
    selectedTourDetails.value = null;
    selectedTourId.value = null;
    photo1Ids.value = [];
    photo2Ids.value = [];
    resetFileUploads.value = true;
    nextTick(() => {
        resetFileUploads.value = false;
    });
};

const markTourAsPrepared = async () => {
    try {
        const payload = {
            isPrepared: true,
            photo1_prepas: photo1Ids.value[0] || null,
            photo2_prepas: photo2Ids.value[0] || null,
        };
        await axios.patch(`admin-explo/tournees/${selectedTourId.value}/`, payload);
        toastRef.value.add({ severity: 'success', summary: 'Succès', detail: 'Tournée marquée comme préparée avec succès.', life: 5000 });
        await fetchTourData();
        resetForm();
    } catch (error) {
        const errorMessage = error.response?.data?.error ?? 'Erreur lors de la mise à jour de la tournée.';
        toastRef.value.add({ severity: 'error', summary: 'Erreur', detail: errorMessage, life: 5000 });
        console.error('Erreur lors de la mise à jour de la tournée:', error);
    }
};

// Modifier le watch
watch(selectedTourId, (newId) => {
 if (newId) fetchTourDetails(newId);
 else selectedTourDetails.value = null;
});

onMounted(fetchTourData);
</script>


<style scoped>
.w-full {
    width: 100%;
}

.p-mb-3 {
    margin-bottom: 1rem;
}

.p-mt-4 {
    margin-top: 1.5rem;
}

/* Styles pour la Dropdown */
.custom-dropdown :deep(.p-dropdown) {
    font-size: 1.2rem;
    /* Taille de base pour tout le composant */
    font-weight: bold;
    /* Gras pour le label */
}

.custom-dropdown :deep(.p-dropdown-label),
.custom-dropdown :deep(.p-dropdown-item) {
    padding: 0.75rem 1rem;
    font-size: 1.2rem;
    /* Même taille pour le label et les items */
    font-weight: bold;
    /* Gras pour le label et les items */
}

.custom-dropdown :deep(.p-dropdown-trigger) {
    padding: 0 1rem;
    display: flex;
    align-items: center;
    font-weight: bold;
    /* Gras pour le label */
}

.custom-dropdown :deep(.p-dropdown-panel) {
    font-size: 1.2rem;
    /* Assure que le panel utilise la même taille de police */
}

.custom-dropdown :deep(.p-dropdown-items) {
    padding: 0.5rem 0;
    /* Ajoute un peu d'espace en haut et en bas de la liste */
}

.custom-dropdown :deep(.p-dropdown-item) {
    margin: 0;
    /* Réinitialise les marges pour les items */
}

/* Autres styles existants */
.custom-quantity-badge {
    width: 2.5em;
    height: 2.5em;
    font-size: 1.2em;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-right: 0.5em;
    transition: all 0.3s ease;
    border-radius: 8px;
    padding: 0;
}

.product-item.selected .custom-quantity-badge {
    border-radius: 50%;
}

.product-name {
    font-size: 1.2rem;
    font-weight: bold;
}

.upload-buttons-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    margin-top: 1.5rem;
}

.product-item {
    cursor: pointer;
    transition: background-color 0.3s;
    display: flex;
    align-items: center;
}
</style>